import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from "@angular/core";
import { HitlResumePayload } from "../../model/interrupt.types";
import { HitlConfirmCardComponent } from "../hitl/hitl-confirm-card/hitl-confirm-card.component";
import { HitlSelectionCardComponent } from "../hitl/hitl-selection-card/hitl-selection-card.component";
import { HitlFormCardComponent } from "../hitl/hitl-form-card/hitl-form-card.component";
import { HitlToolCallCardComponent } from "../hitl/hitl-tool-call-card/hitl-tool-call-card.component";
import { ChatMessageListComponent } from "../chat-message-list/chat-message-list.component";
import { ChatInputComponent } from "../chat-input/chat-input.component";
import { ChatErrorBannerComponent } from "../chat-error-banner/chat-error-banner.component";
import { DebugMockPanelComponent } from "../debug/mock-panel/debug-mock-panel.component";
import { BackgroundJobPanelComponent } from "../background-job-panel/background-job-panel.component";
import { HitlService } from "../../services/hitl.service";

/**
 * Presentational shell for the chat layout.
 * Composes: message list, error banner, HITL wizard, and input.
 *
 * The HITL wizard steps through clarifications one at a time.
 * When the last entry is answered, clarificationSubmit emits the full resume payload.
 */
@Component({
  selector: "lib-chat-view",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HitlConfirmCardComponent,
    HitlSelectionCardComponent,
    HitlFormCardComponent,
    HitlToolCallCardComponent,
    ChatMessageListComponent,
    ChatInputComponent,
    ChatErrorBannerComponent,
    DebugMockPanelComponent,
    BackgroundJobPanelComponent,
  ],
  templateUrl: "./chat-view.component.html",
  styleUrl: "./chat-view.component.scss",
})
export class ChatViewComponent {
  readonly messages = input<any[]>([]);

  readonly showCursor = input<boolean>(false);

  readonly runError = input<string | null>(null);

  /** Emitted once all clarification entries have been answered. */
  readonly clarificationSubmit = output<HitlResumePayload>();

  readonly errorRetry = output<void>();

  protected readonly hitlService = inject(HitlService);

  protected readonly errorDismissed = signal(false);

  protected readonly currentEntry = computed(() => this.hitlService.currentEntry());

  protected readonly showProgress = computed(() => this.hitlService.showProgress());

  protected readonly currentStep = computed(() => this.hitlService.currentStep());

  protected readonly totalEntries = computed(() => this.hitlService.totalEntries());

  protected readonly displayText = computed(() => this.hitlService.displayText());

  protected readonly visibleError = computed(() => {
    if (this.errorDismissed()) return null;
    return this.runError();
  });

  protected onErrorDismiss(): void {
    this.errorDismissed.set(true);
  }

  protected onErrorRetry(): void {
    this.errorDismissed.set(false);
    this.errorRetry.emit();
  }

  protected onHitlSubmit(value: string | string[]): void {
    const resumePayload = this.hitlService.submitAnswer(value);
    if (resumePayload !== null) {
      this.clarificationSubmit.emit(resumePayload);
    }
  }

  /** Sends the single-entry refusal resume for the current ask (HITL contract §4). */
  protected onHitlCancel(): void {
    const payload = this.hitlService.cancelCurrent();
    if (payload !== null) {
      this.clarificationSubmit.emit(payload);
    }
  }
}
